import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type Hoja = components['schemas']['HojaRead']
export type FilaCumplimiento = components['schemas']['FilaCumplimientoRead']
export type BloqueCumplimiento = components['schemas']['BloqueCumplimientoRead']
export type NoVerificable = components['schemas']['NoVerificableCumplimientoRead']
export type RejillaColumnas = components['schemas']['RejillaColumnasRead']
export type FilaRejilla = components['schemas']['FilaRejillaRead']
export type ColumnaRejilla = components['schemas']['ColumnaRejillaRead']
export type CruceRejilla = components['schemas']['CruceRejillaRead']
export type Pedido = components['schemas']['PedidoDeSoporteRead']
export type PedidoPorPersona = components['schemas']['PedidoPorPersonaRead']

const REJILLA_POR_PAGINA = 25

/** R6: diez y no las 20 de la casa porque cada fila es alta. */
export const CUMPLIMIENTO_POR_PAGINA = 10

function hojaBase(businessId: string, employeeId: string) {
  return `/businesses/${businessId}/sgsst/employees/${employeeId}/cumplimiento`
}

function hojaKey(businessId: string | undefined, employeeId: string | undefined) {
  return ['cumplimiento', businessId, employeeId]
}

function rejillaKey(businessId: string | undefined, positionId: string | undefined) {
  return ['rejilla-cargo', businessId, positionId]
}

/** Viaja entera y se pagina en el cliente (excepción R6): el techo es el catálogo (~25 filas), y paginar por
 * bloque serviría números de otra foto (D11). */
export function useCumplimiento(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: hojaKey(businessId, employeeId),
    queryFn: async () => (await api.get<Hoja>(hojaBase(businessId!, employeeId!))).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** Devuelve la hoja entera y no la fila: los números, el color y el veredicto se mueven con cada acto. */
function useActoDeFila<T>(businessId: string, employeeId: string, ruta: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {requisitoId: string; cuerpo?: T}) =>
      (await api.post<Hoja>(`${hojaBase(businessId, employeeId)}/${input.requisitoId}/${ruta}`, input.cuerpo ?? {}))
        .data,
    onSuccess: (data) => {
      queryClient.setQueryData(hojaKey(businessId, employeeId), data)
      queryClient.invalidateQueries({queryKey: ['rejilla-cargo', businessId]})
    },
  })
}

export function useConfirmarFila(businessId: string, employeeId: string) {
  return useActoDeFila<never>(businessId, employeeId, 'confirmar')
}

export function useNoCorresponde(businessId: string, employeeId: string) {
  return useActoDeFila<{motivo: string}>(businessId, employeeId, 'no-corresponde')
}

export function useRechazarFila(businessId: string, employeeId: string) {
  return useActoDeFila<{motivo: string}>(businessId, employeeId, 'rechazar')
}

export function useVolverAExigir(businessId: string, employeeId: string) {
  return useActoDeFila<never>(businessId, employeeId, 'volver-a-exigir')
}

export function usePedirSoporte(businessId: string, employeeId: string) {
  return useActoDeFila<{plazo: string; nota: string}>(businessId, employeeId, 'pedir')
}

/** Devuelve el acuse (a cuántas filas alcanzó) y no la hoja, que se invalida aparte. */
export function usePedirLoQueFalta(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cuerpo: {plazo: string; nota: string}) =>
      (await api.post<PedidoPorPersona>(`${hojaBase(businessId, employeeId)}/pedir-lo-que-falta`, cuerpo)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: hojaKey(businessId, employeeId)})
      queryClient.invalidateQueries({queryKey: ['rejilla-cargo', businessId]})
    },
  })
}

/** Aparte de las filas porque `Page` no tiene dónde poner columnas dinámicas; los números son del cargo entero. */
export function useRejillaColumnas(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: [...rejillaKey(businessId, positionId), 'columnas'],
    queryFn: async () =>
      (await api.get<RejillaColumnas>(`/businesses/${businessId}/sgsst/positions/${positionId}/rejilla/columnas`)).data,
    enabled: !!businessId && !!positionId,
  })
}

/** R6: siempre paginadas; los totales del cargo vienen de `useRejillaColumnas`. */
export function useRejilla(
  businessId: string | undefined,
  positionId: string | undefined,
  params: {page: number; search: string; color?: string}
) {
  return usePagedList<FilaRejilla>(
    rejillaKey(businessId, positionId),
    businessId && positionId ? `/businesses/${businessId}/sgsst/positions/${positionId}/rejilla` : undefined,
    // U19: el filtro va al servidor; filtrar en el cliente dejaría el total del pie equivocado.
    {page: params.page, search: params.search, extra: {color: params.color}},
    REJILLA_POR_PAGINA
  )
}

export function usePedirEnBloque(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {requisitoId: string; plazo: string; nota: string}) =>
      (
        await api.post<Pedido>(`/businesses/${businessId}/sgsst/positions/${positionId}/rejilla/pedir`, {
          requisito_id: input.requisitoId,
          plazo: input.plazo,
          nota: input.nota,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: rejillaKey(businessId, positionId)})
      queryClient.invalidateQueries({queryKey: ['cumplimiento', businessId]})
    },
  })
}

export type Choques = components['schemas']['ChoquesRead']
export type Choque = components['schemas']['ChoqueRead']
export type ChoqueSalida = components['schemas']['ChoqueSalida']

function choquesBase(businessId: string, employeeId: string) {
  return `/businesses/${businessId}/sgsst/employees/${employeeId}/choques`
}

function choquesKey(businessId: string | undefined, employeeId: string | undefined) {
  return ['choques', businessId, employeeId]
}

/** D12: a quien no custodia solo le llega el conteo (`detalle_reducido`), con la lista vacía. */
export function useChoques(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: choquesKey(businessId, employeeId),
    queryFn: async () => (await api.get<Choques>(choquesBase(businessId!, employeeId!))).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** Invalida la hoja: resolver un choque le devuelve el color a la fila del concepto. */
export function useResolverChoque(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {choqueId: string; salida: ChoqueSalida; motivo: string}) =>
      (
        await api.post<Choques>(`${choquesBase(businessId, employeeId)}/${input.choqueId}/resolver`, {
          salida: input.salida,
          motivo: input.motivo,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(choquesKey(businessId, employeeId), data)
      queryClient.invalidateQueries({queryKey: hojaKey(businessId, employeeId)})
    },
  })
}
